import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, filter, Observable, of, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { AppConstants } from '../../../../SGRE-shared/constants/app-constant';
import { GlobalService } from '../../../../SGRE-shared/services/global.service';
import { SavedCartsService } from '../../../save-cart/services/saved-carts.service';
import { CartsByLegalEntityState, CreateNewCartState } from '../../../../SGRE-shared/store/shared-store/shared.state';
import {
  CustomSavedCartsData,
  CustomSetActiveCartByMiniCart,
} from '../../../save-cart/store/state/cart-details.state';
import { StorageService } from '../../../../SGRE-shared/services/storage.service';
import { CardTemplateTrackingService } from '../../../../SGRE-shared/services/cart-template-analytics.service';
import { PayloadInfo } from '../../../save-cart/models/manage-carts.model';

@Component({
  selector: 'app-cart-selector',
  templateUrl: './cart-selector.component.html',
  styleUrl: './cart-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartSelectorComponent implements OnInit, OnDestroy {

  public newCartPopupForm: FormGroup;
  public cartsList$: Observable<CartsByLegalEntityState>;
  public toggleCarts = false;
  public fromToggle = false;
  public legalEntityList: string[] = [];
  shouldCreateNewCartFlag = false;
  showNewCartModal = false;
  public unsubscribe$ = new Subject<void>();

  get cartName() { return this.newCartPopupForm?.get('cartName'); }
  get legalEntity() { return this.newCartPopupForm?.get('legalEntity'); }

  @HostListener('document:click', ['$event']) onDocument(event: MouseEvent) {
    if (this.toggleCarts && !this.fromToggle)
      this.toggleCarts = !this.toggleCarts;
    this.fromToggle = false;
  }

  constructor(
    private globalService: GlobalService,
    private router: Router,
    private savedCartsService: SavedCartsService,
    private storageService: StorageService,
    private cardTemplateTrackingService: CardTemplateTrackingService,
  ) {
    this.newCartPopupForm = new FormGroup({
      cartName: new FormControl('', [Validators.required]),
      legalEntity: new FormControl('', [Validators.required]),
    });
    // Options for legalEntity dropdown within the 'New Cart' popup
    this.legalEntityList = this.storageService.userLegalEntities?.map((item) => item.name) || [];
  }

  /** -- OOTB Implementation -- */
  ngOnInit(): void {
    this.loadMiniCart();
    this.getMiniCartDetails();
    this.listenToStoreUpdates();
  }

  loadMiniCart(): void {
    this.globalService.loadCartsListByLegalEntity();
  }

  getMiniCartDetails(): void {
    this.cartsList$ = this.globalService.getCartsData()
      .pipe(
        tap((data) => {
          if (data?.loading === false && !data?.error) {
            if (this.shouldCreateNewCart()) {
              this.shouldCreateNewCartFlag = true;
              const legalEntityId = this.storageService.defaultLegalEntity?.uid ?? '';
              const cartName = PayloadInfo.NEW_CART;
              this.createNewCart(legalEntityId, cartName);
            }
          }
        }),
        takeUntil(this.unsubscribe$)
      );
  }

  listenToStoreUpdates(): void {
    this.globalService.getNewCartData()
      .pipe(
        filter(data => !!data?.success || !!data?.error),
        switchMap((data: CreateNewCartState) => {
          if (!!data?.success) {
            if (this.showNewCartModal) { this.showNewCartModal = false; }
            const uIdVal: string = this.getLegalEntityId();
            if (!!uIdVal && this.storageService.defaultLegalEntity?.uid !== uIdVal) {
              this.globalService._updateLegalEntity(uIdVal);
            } else {
              if (this.shouldCreateNewCartFlag) {
                this.globalService.loadCartsListByLegalEntity();
                this.shouldCreateNewCartFlag = false;
              } else {
                this.globalService.dispatchUpdateLegalEntity();
              }
            }
          }
          if (!!data?.error) {
            this.handleCreateCartError();
          }
          return of(null);
        }),
        catchError((err) => {
          this.handleCreateCartError();
          return throwError(() => err);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  handleCreateCartError(): void {
    this.globalService.loadingSubject.next(false);
    if (this.showNewCartModal) { this.showNewCartModal = false; }
  }

  getLegalEntityId(): string {
    const legalEntityName = this.legalEntity?.value;
    if (!legalEntityName) return '';
    return this.storageService.userLegalEntities?.find(item => item.name === legalEntityName)?.uid || '';
  }

  // Returns true if there are no carts or no active cart exists.
  shouldCreateNewCart(): boolean {
    const hasNoCarts = !this.storageService?.carts?.length;
    const hasNoActiveCart = this.storageService?.carts?.findIndex(cart => !!cart?.isActiveCart) === -1;
    const hasDefaultLegalEntityUid = !!this.storageService?.defaultLegalEntity?.uid?.length;

    return (hasNoCarts || hasNoActiveCart) && hasDefaultLegalEntityUid;
  }

  createNewCart(legalEntityId: string, cartName: string): void {
    this.globalService.createCart(legalEntityId, cartName);
  }

  toggle(): void {
    this.fromToggle = true;
    this.toggleCarts = !this.toggleCarts;
  }

  navigateToCart(): void {
    const clickTarget = event?.target as HTMLElement;

    // Get current active cart for tracking
    const activeCart = this.storageService?.carts?.find(cart => cart?.isActiveCart === true);
    if (activeCart) {
      const cartType = this.cardTemplateTrackingService.determineCartType(activeCart);
      this.cardTemplateTrackingService.trackCartNavigation(
        activeCart.code,
        cartType,
        clickTarget
      );
    }
    this.router.navigate([AppConstants.routeUrls.cart]);
  }

  changeCart(index): void {
    this.toggleCarts = !this.toggleCarts;
    let cartsList: CustomSavedCartsData[] = (this.storageService.carts?.length > 0) ? this.storageService.carts : [];
    if (cartsList?.length > 0) {
      this.globalService.loadingSubject.next(true);
      const cartObj = cartsList[index + 1];
      cartsList = cartsList.filter((v, i) => i !== index + 1);
      cartsList = [cartObj, ...cartsList];
      this.savedCartsService.setActiveCartByMiniCart(
        cartObj as CustomSetActiveCartByMiniCart
      );
    }
  }

  onNewCartClick(): void {
    this.cartName.reset();
    this.legalEntity.setValue(this.storageService.defaultLegalEntity?.name ?? '');

    // Show the modal after setting the legal entity
    this.showNewCartModal = true;
  }

  onManageCartsClick(): void {
    this.router.navigate([AppConstants.routeUrls.manageCarts]);
    this.toggleCarts = !this.toggleCarts;
  }

  onNewCartConfirm(): void {
    if (this.newCartPopupForm.invalid) {
      this.newCartPopupForm.markAllAsTouched();
      return;
    }

    this.globalService.loadingSubject.next(true);
    const legalEntityId: string = this.getLegalEntityId();
    this.createNewCart(legalEntityId, this.cartName.value);
  }

  onCloseNewCartModal(): void {
    this.newCartPopupForm.reset();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
    this.globalService.clearMessagesOnDestroy();
    document.removeEventListener('click', this.onDocument);
  }
}
